import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "./Reveal";

const tiers = [
  {
    name: "Single item",
    price: "$249",
    load: 1,
    capacity: "Minimum",
    desc: "One couch, one appliance, or a few odds and ends.",
  },
  {
    name: "Small room",
    price: "$549",
    load: 2,
    capacity: "¼ truckload",
    desc: "A room's worth — sofa, table, the usual suspects.",
    featured: true,
  },
  {
    name: "Garage cleanout",
    price: "$849",
    load: 3,
    capacity: "½ truckload",
    desc: "A garage or bedroom set that's been piling up.",
  },
  {
    name: "Full cleanout",
    price: "$1,100+",
    load: 4,
    capacity: "Full truck +",
    desc: "Whole-home, basement or estate. Every last corner.",
  },
];

const includes = [
  "Donation & responsible disposal",
  "All loading & heavy lifting",
  "Transportation",
  "Sweep-up when we're done",
];

const LoadMeter = ({ load, featured }) => (
  <div className="flex items-end gap-1" aria-hidden>
    {[1, 2, 3, 4].map((n) => (
      <span
        key={n}
        className={`w-2 rounded-full transition-colors duration-300 ${
          n <= load
            ? featured
              ? "bg-violet"
              : "bg-charcoal"
            : featured
            ? "bg-violet/20"
            : "bg-stone"
        }`}
        style={{ height: `${8 + n * 5}px` }}
      />
    ))}
  </div>
);

export default function Pricing({ onStart }) {
  return (
    <section id="pricing" className="py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left rail */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet/70">Pricing</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-4xl leading-[1.02] tracking-tight text-charcoal sm:text-5xl lg:text-6xl text-balance">
                Pickup starts at <span className="text-violet">$249</span>.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-charcoal/60">
                One flat price based on what's actually in the room — never by the
                hour. Snap a photo for an exact quote in seconds.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-9 rounded-[1.8rem] bg-white/60 p-7">
                <p className="text-sm font-semibold uppercase tracking-wide text-charcoal/50">
                  Every pickup includes
                </p>
                <ul className="mt-4 space-y-3">
                  {includes.map((inc) => (
                    <li key={inc} className="flex items-center gap-3 text-charcoal">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage/50">
                        <Check size={12} />
                      </span>
                      {inc}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-sm text-charcoal/45">No fuel charges. No surprises at the door.</p>
              </div>
            </Reveal>
          </div>

          {/* Tier rows */}
          <div className="lg:col-span-7">
            <div className="space-y-3">
              {tiers.map((t, i) => (
                <Reveal key={t.name} delay={i * 0.06}>
                  <button
                    onClick={onStart}
                    data-testid={`pricing-${t.name.toLowerCase().replace(/\s+/g, "-")}-cta`}
                    className={`group grid w-full grid-cols-[auto_1fr_auto] items-center gap-5 rounded-[1.8rem] px-6 py-6 text-left transition-all duration-300 hover:-translate-y-0.5 sm:px-8 sm:py-7 ${
                      t.featured
                        ? "bg-lavender/60 ring-1 ring-violet/20 hover:bg-lavender/80"
                        : "bg-white/55 hover:bg-white/90"
                    }`}
                  >
                    {/* load meter */}
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-canvas/70">
                      <LoadMeter load={t.load} featured={t.featured} />
                    </div>

                    {/* name + desc */}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <h3 className="font-display text-2xl text-charcoal sm:text-[1.7rem]">{t.name}</h3>
                        <span className="rounded-full bg-canvas/80 px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-charcoal/55">
                          {t.capacity}
                        </span>
                        {t.featured && (
                          <span className="rounded-full bg-violet px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-white">
                            Most booked
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 truncate text-charcoal/55">{t.desc}</p>
                    </div>

                    {/* price + arrow */}
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-display text-3xl tracking-tight text-charcoal sm:text-4xl">{t.price}</p>
                        {t.price !== "Custom" && (
                          <p className="text-xs text-charcoal/40">starting</p>
                        )}
                      </div>
                      <span
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all duration-300 group-hover:rotate-0 ${
                          t.featured
                            ? "bg-violet text-white"
                            : "bg-charcoal text-canvas group-hover:bg-violet"
                        }`}
                      >
                        <ArrowUpRight size={18} />
                      </span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <p className="mt-6 text-center text-sm text-charcoal/45 sm:text-left">
                Not sure which fits?{" "}
                <button onClick={onStart} className="font-semibold text-violet underline-offset-4 hover:underline">
                  Get an instant quote
                </button>{" "}
                — the photo decides.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
